import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { SkeletonChart } from "../components/shared/Skeleton";
import InfoTip from "../components/shared/InfoTip";
import AdminHeader from "../components/AdminHeader";
import ClientHeader from "../components/ClientHeader";
import LiveFeed from "../components/cdp/LiveFeed";
import CdpSessionTable from "../components/cdp/CdpSessionTable";
import CdpSessionDetail from "../components/cdp/CdpSessionDetail";
import CdpVisitorSegments from "../components/cdp/CdpVisitorSegments";
import CdpChannelsTab from "../components/cdp/CdpChannelsTab";
import CdpContentTab from "../components/cdp/CdpContentTab";
import CdpPeopleTab from "../components/cdp/CdpPeopleTab";
import PersonProfile from "../components/cdp/PersonProfile";
import CdpAiTechTab from "../components/cdp/CdpAiTechTab";
import CdpInsightsTab from "../components/cdp/CdpInsightsTab";
import { KpiCard } from "../components/cdp/CdpKpiCard";
import PanelTakeaway from "../components/shared/PanelTakeaway";
import NavSidebar from "../components/NavSidebar";
import { usePageMeta } from "../components/DashboardShell";
import { useFeatures } from "../contexts/FeaturesContext";
import { useTheme } from "../contexts/ThemeContext";
import { useView } from "../contexts/ViewContext";
import {
  getCdpKpis,
  getCdpTrend,
  getCdpFunnel,
  getGuestIntelSentences,
} from "../api";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";

/* ----------------------------- channel colors ----------------------------- */
const CHANNEL_COLORS = {
  "Paid Search": "#2563EB",
  "Paid Social": "#7C3AED",
  "Organic Search": "#059669",
  "Organic Social": "#0D9488",
  "Direct": "#6B7280",
  "Maps": "#D97706",
  "Email": "#DB2777",
  "AI": "#4F46E5",
  "Referral": "#B45309",
  "Booking Platform": "#0891B2",
  "Display": "#DC2626",
  "Affiliate": "#65A30D",
};

/* ----------------------------- helpers ----------------------------- */
const fmtNum = (n) => {
  if (n == null) return "0";
  return n.toLocaleString();
};

const fmtPct = (n) => {
  if (n == null) return "0.0%";
  return `${n.toFixed(1)}%`;
};

// P1-1 (punch list): cap conversion rate at 100% so per-page anomalies (2 clicks ÷ 1 visitor = 200%)
// don't display as misleading "200% Conv Rate".
const fmtPctCapped = (n) => {
  if (n == null) return "0.0%";
  return `${Math.min(n, 100).toFixed(1)}%`;
};

const fmtUsd = (n) => {
  if (n == null) return "$0";
  if (Math.abs(n) >= 1000) return `$${Math.round(n).toLocaleString()}`;
  return `$${n.toFixed(2)}`;
};

const fmtDay = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return `${d.getMonth() + 1}/${d.getDate()}`;
};

// KPI card anatomy (computeDelta, DeltaBadge, KPI_* tokens, KpiCard, HintButton)
// now lives in ../components/cdp/CdpKpiCard.js — imported near the top. CdpPeopleTab
// shares it so the People tab's "Identified Visitors" stat matches these tiles
// byte-for-byte (accent border, watermark, .gi-card-label, .gi-number, DeltaBadge).
// (HintButton + KpiCard moved to ../components/cdp/CdpKpiCard.js)


/* ============================= COMPONENT ============================= */
export default function CdpDashboardPage() {
  usePageMeta({ headerActive: 'Visitors', sidebarActive: 'visitors' });
  const navigate = useNavigate();
  const view = useView();
  const { isDarkMode } = useTheme();
  const { hasFeature } = useFeatures();

  const username = localStorage.getItem("username") || "User";
  const isClient = view.isClient;
  const dashboardPath = isClient ? "/client-dashboard" : "/dashboard";

  // Single source of truth: read client + dates directly from ViewContext (matches Route Intel pattern)
  const selectedClientId = view.supabaseClientId || null;

  // Observe the main content's height so the LiveFeed sidebar can match it
  // exactly. Recalculates on resize, layout shifts, and content changes —
  // safe across all breakpoints (the sidebar itself is hidden below `lg`).
  const mainRef = useRef(null);
  const [mainHeight, setMainHeight] = useState(null);
  useEffect(() => {
    if (!mainRef.current) return;
    const ro = new ResizeObserver(([entry]) => {
      setMainHeight(Math.round(entry.contentRect.height));
    });
    ro.observe(mainRef.current);
    return () => ro.disconnect();
  }, []);

  // Dates come from the global header picker (ViewContext) only — the page has
  // no module-level picker, so one range/label drives everything.
  const rangeStart = view.rangeStart;
  const rangeEnd = view.rangeEnd;

  // P1-4 (punch list 2026-06-05): exclude bot/proxy traffic from headline KPIs by default.
  // Backend default is exclude-bots; toggling this ON adds ?include_bots=true to every CDP call.
  // Persist to localStorage so the user's preference survives reloads.
  const [includeBots, setIncludeBots] = useState(() => {
    try { return localStorage.getItem("cdp_include_bots") === "true"; }
    catch (_) { return false; }
  });
  const toggleIncludeBots = useCallback(() => {
    setIncludeBots((prev) => {
      const next = !prev;
      try { localStorage.setItem("cdp_include_bots", String(next)); } catch (_) {}
      return next;
    });
  }, []);
  const apiOpts = useMemo(() => ({ includeBots }), [includeBots]);

  // Epic 7 (Tier 6) — Live Feed demoted to a collapsible panel, OFF by default.
  // It's engaging but the least decision-relevant view; it shouldn't be the
  // first thing on screen. The fixed sidebar + its layout spacer only render
  // when the user opts in, so main content gets full width otherwise (and the
  // feed's realtime polling stays off until then).
  const [liveFeedOpen, setLiveFeedOpen] = useState(false);

  // ─── KPI State ─────────────────────────────────────────────────────────
  const [kpis, setKpis] = useState(null);
  const [kpisLoading, setKpisLoading] = useState(false);

  // ─── Trend State ───────────────────────────────────────────────────────
  const [trend, setTrend] = useState([]);
  const [trendLoading, setTrendLoading] = useState(false);
  const [trendLines, setTrendLines] = useState({ visitors: true, book_clicks: true });

  // ─── Funnel State ──────────────────────────────────────────────────────
  const [funnel, setFunnel] = useState(null);
  const [funnelLoading, setFunnelLoading] = useState(false);

  // 2026-08-14 (Epic 10) — AI sentence layer. Fetched in parallel with
  // KPIs/trend/funnel. Empty map = no sentences for this date range (normal
  // for custom ranges). Never blocks rendering.
  const [say, setSay] = useState({});

  // ─── Session Detail State ──────────────────────────────────────────
  const [selectedSession, setSelectedSession] = useState(null);

  // ─── Segment Filter State ──────────────────────────────────────────
  const [activeSegment, setActiveSegment] = useState(null);

  // ─── Person Profile State ──────────────────────────────────────────
  const [selectedPerson, setSelectedPerson] = useState(null); // { personId, cvId }

  // ─── Data Fetching ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!selectedClientId || !rangeStart || !rangeEnd) return;

    // 2026-08-14 (Epic 13) — parallel-fetch KPIs, trend, and funnel.
    // Was: three separate async functions called sequentially (staircasing —
    // each waited for the previous to start). Now: Promise.all fires all three
    // at once. Each setter handles its own error so one failure doesn't block
    // the others. Loading flags set together, cleared together.
    setKpisLoading(true);
    setTrendLoading(true);
    setFunnelLoading(true);

    const p1 = getCdpKpis(selectedClientId, rangeStart, rangeEnd, apiOpts)
      .then(r => setKpis(r?.data ?? null))
      .catch(e => { console.error("CDP KPI error:", e); setKpis(null); })
      .finally(() => setKpisLoading(false));

    const p2 = getCdpTrend(selectedClientId, rangeStart, rangeEnd, apiOpts)
      .then(r => setTrend(r?.data ?? []))
      .catch(e => { console.error("CDP trend error:", e); setTrend([]); })
      .finally(() => setTrendLoading(false));

    const p3 = getCdpFunnel(selectedClientId, rangeStart, rangeEnd, apiOpts)
      .then(r => setFunnel(r?.data ?? null))
      .catch(e => { console.error("CDP funnel error:", e); setFunnel(null); })
      .finally(() => setFunnelLoading(false));

    // 2026-08-14 (Epic 10) — fetch AI sentences in the same parallel batch.
    // Never blocks — returns empty map on error/missing table.
    const p4 = getGuestIntelSentences(selectedClientId, rangeStart, rangeEnd)
      .then(r => setSay(r?.data ?? {}))
      .catch(() => setSay({}));

    Promise.all([p1, p2, p3, p4]);
  }, [selectedClientId, rangeStart, rangeEnd, apiOpts]);

  // ─── KPI Cards Data ────────────────────────────────────────────────────
  const kpiCards = useMemo(() => {
    const c = kpis?.current || {};
    const p = kpis?.previous || {};
    const visitors = c.total_visitors || 0;
    const bookClicks = c.book_now_clicks || 0;
    const convRate = visitors > 0 ? (bookClicks / visitors) * 100 : 0;
    const pVisitors = p.total_visitors || 0;
    const pBookClicks = p.book_now_clicks || 0;
    const pConvRate = pVisitors > 0 ? (pBookClicks / pVisitors) * 100 : 0;

    // Per-day series from the same fn_get_cdp_trend payload that drives the
    // Daily Trend chart — every card gets its own real metric (no proxies).
    const spark = (key) => trend.map((d) => ({ v: Number(d[key]) || 0 }));
    const sparkAvgValue = trend.map((d) => ({ v: d.bookings > 0 ? (d.revenue || 0) / d.bookings : 0 }));

    // Bookings-style KPI tiles: accent border + watermark icon per variant,
    // a DeltaBadge pill in the title row, sparkline at the bottom. Money-first
    // ordering retained (Revenue + Bookings lead). The Bookings tile carries
    // the pacing sentence (say['pacing']) via the `takeaway` slot; PanelTakeaway
    // no-ops when absent (custom date range) so there is no gap.
    //
    // Uniform hero sizing (2026-10): ALL ten tiles render their value at
    // .gi-hero-number (via hero: true) so the Tier 2 row reads as one uniform
    // band instead of a hero/secondary split. The money tiles still lead the
    // row by position; the hero/secondary *type* tier is intentionally
    // flattened here — see the matching test in CdpDashboardPage.test.js.
    return [
      { label: "Revenue", value: fmtUsd(c.revenue || 0), current: c.revenue || 0, previous: p.revenue || 0, variant: "warning", icon: "dollar", subtitle: "Booking value captured", spark: spark("revenue"), tooltip: "Booking value captured at confirmation (site_total). Covers the bookings where full confirmation data was recorded (~94% of total).", hero: true },
      { label: "Bookings", value: fmtNum(c.completed_bookings || 0), current: c.completed_bookings || 0, previous: p.completed_bookings || 0, variant: "success", icon: "booking", subtitle: "Completed reservations", spark: spark("bookings"), tooltip: "Completed reservations. Count reflects all bookings; tracked revenue covers the subset with captured booking value.", hero: true },
      { label: "Book Now Clicks", value: fmtNum(bookClicks), current: bookClicks, previous: pBookClicks, variant: "primary", icon: "click", subtitle: "Reservation button taps", spark: spark("book_clicks"), tooltip: "Times a visitor clicked a Book Now / reservation button.", hero: true },
      { label: "Visitors", value: fmtNum(visitors), current: visitors, previous: pVisitors, variant: "muted", icon: "users", subtitle: "Unique by browser ID", spark: spark("visitors"), tooltip: "Unique people who visited, by browser ID (cv_id). Counts ad-blocked & cookieless visitors GA misses.", hero: true },
      { label: "Sessions", value: fmtNum(c.total_sessions || 0), current: c.total_sessions || 0, previous: p.total_sessions || 0, variant: "subtle", icon: "chart", subtitle: "Distinct visit sessions", spark: spark("sessions"), tooltip: "Distinct visit sessions. One visitor can have several.", hero: true },
      { label: "Known People", value: fmtNum(c.known_people || 0), current: c.known_people || 0, previous: p.known_people || 0, variant: "rose", icon: "users", subtitle: "Matched email or phone", spark: spark("known_visitors"), tooltip: "Visitors we've matched to an email or phone.", hero: true },
      { label: "Video Plays", value: fmtNum(c.video_plays || 0), current: c.video_plays || 0, previous: p.video_plays || 0, variant: "primary", icon: "play", subtitle: "Video starts", spark: spark("video_plays"), tooltip: "Times a visitor started a video.", hero: true },
      { label: "Conversion Rate", value: fmtPctCapped(convRate), current: convRate, previous: pConvRate, variant: "subtle", icon: "percent", subtitle: "Book clicks / visitors", spark: spark("conversion_rate"), tooltip: "Share of visitors who showed booking intent (clicked Book). Capped at 100%.", hero: true },
      { label: "Avg Value", value: fmtUsd(c.avg_value || 0), current: c.avg_value || 0, previous: p.avg_value || 0, variant: "warning", icon: "dollar", subtitle: "Per captured booking", spark: sparkAvgValue, tooltip: "Average value across bookings with captured revenue — not divided by total booking count.", hero: true },
      { label: "Phone Clicks", value: fmtNum(c.phone_clicks || 0), current: c.phone_clicks || 0, previous: p.phone_clicks || 0, variant: "muted", icon: "phone", subtitle: "Phone-number link taps", spark: spark("phone_clicks"), tooltip: "Times a visitor tapped a phone-number link.", hero: true },
    ];
  }, [kpis, trend]);

  // ─── Funnel Stages ─────────────────────────────────────────────────────
  const funnelStages = useMemo(() => {
    if (!funnel) return [];
    const f = funnel;
    const stages = [
      { label: "All Visitors", count: f.visitors || 0, color: "bg-gray-200 dark:bg-gray-700" },
      { label: "Showed Intent", count: f.showed_intent || 0, color: "bg-emerald-200 dark:bg-emerald-800" },
      { label: "Clicked Book", count: f.clicked_book || 0, color: "bg-emerald-400 dark:bg-emerald-600" },
      { label: "Identified", count: f.gave_email || 0, color: "bg-amber-300 dark:bg-amber-700" },
      { label: "Booked", count: f.booked || 0, color: "bg-amber-500 dark:bg-amber-500" },
    ];
    // Compute pass-through rates
    for (let i = 1; i < stages.length; i++) {
      const prev = stages[i - 1].count;
      stages[i].rate = prev > 0 ? ((stages[i].count / prev) * 100).toFixed(1) : "0.0";
    }
    return stages;
  }, [funnel]);

  // ─── Trend toggle ──────────────────────────────────────────────────────
  const toggleTrendLine = (key) => setTrendLines((prev) => ({ ...prev, [key]: !prev[key] }));

  const TREND_LINES = [
    { key: "visitors", label: "Visitors", color: "#6B7280", defaultOn: true },
    { key: "sessions", label: "Sessions", color: "#9CA3AF" },
    { key: "book_clicks", label: "Book Clicks", color: "#059669", defaultOn: true },
    { key: "bookings", label: "Bookings", color: "#D97706" },
    { key: "phone_clicks", label: "Phone", color: "#2563EB" },
    { key: "video_plays", label: "Video", color: "#7C3AED" },
    { key: "from_facebook", label: "Facebook", color: "#7C3AED" },
    { key: "from_organic", label: "Organic", color: "#059669" },
    { key: "from_ai", label: "AI", color: "#4F46E5" },
  ];

  /* ============================= RENDER ============================= */
  return (
    <>
        {/* ─── LIVE FEED (Tier 6 — demoted, collapsible, OFF by default) ─── */}
        {liveFeedOpen && (
          <LiveFeed
            clientId={selectedClientId}
            channelColors={CHANNEL_COLORS}
            panelHeight={mainHeight}
          />
        )}

        {/* ─── MAIN CONTENT ─── */}
        <main ref={mainRef} className="flex-1 min-w-0">
          <div className="px-6 py-6 space-y-6">

            {/* ─── VERDICT (Epic 10) — AI-generated overall takeaway.
                Hidden entirely when absent. Never an empty hero or spinner. ─── */}
            {say?.verdict && (
              <PanelTakeaway panelKey="verdict" variant="verdict" say={say} />
            )}

            {/* ─── PAGE HEADER + DATE PICKER (P0-3) ─── */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <h1 className="text-xl font-semibold text-gray-900 dark:text-white">Visitor Intelligence</h1>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  Visitors who came to your site — counted by browser ID. Includes ad-blocked &amp; cookieless traffic that GA misses.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 w-full sm:w-auto">
                {/* P1-4: bot-exclusion toggle. Default OFF (excludes bots) to match backend default
                    so the KPIs the user sees aren't inflated by crawler/proxy traffic. */}
                <button
                  type="button"
                  onClick={toggleIncludeBots}
                  className={`px-3 py-2 rounded-lg text-xs font-medium border transition-colors whitespace-nowrap ${
                    includeBots
                      ? "bg-amber-50 dark:bg-amber-900/30 border-amber-200 dark:border-amber-700 text-amber-700 dark:text-amber-300"
                      : "bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
                  }`}
                  title={
                    includeBots
                      ? "Currently including bot/proxy traffic. Click to exclude crawler / proxy hits from headline KPIs."
                      : "Bots & proxy traffic are excluded (default). Click to include them in headline KPIs."
                  }
                >
                  <span className="mr-1.5">{includeBots ? "⚠" : "✓"}</span>
                  {includeBots ? "Bots: included" : "Bots: excluded"}
                </button>
                {/* Tier 6 — Live Feed toggle (off by default = demoted). */}
                <button
                  type="button"
                  onClick={() => setLiveFeedOpen((v) => !v)}
                  className={`px-3 py-2 rounded-lg text-xs font-medium border transition-colors whitespace-nowrap ${
                    liveFeedOpen
                      ? "bg-emerald-50 dark:bg-emerald-900/30 border-emerald-200 dark:border-emerald-700 text-emerald-700 dark:text-emerald-300"
                      : "bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
                  }`}
                  title={liveFeedOpen ? "Live feed is open. Click to hide it." : "Show the live visitor feed (engaging, but least decision-relevant)."}
                >
                  <span className="mr-1.5">●</span>
                  {liveFeedOpen ? "Live feed: on" : "Live feed"}
                </button>
              </div>
            </div>

            {/* --- TIER 2: MONEY KPIs (Bookings-style tiles) --- */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {kpiCards.map((card) => (
                <KpiCard
                  key={card.label}
                  title={card.label}
                  value={card.value}
                  subtitle={card.subtitle}
                  current={card.current}
                  previous={card.previous}
                  variant={card.variant}
                  icon={card.icon}
                  loading={kpisLoading}
                  tooltip={card.tooltip}
                  sparkData={card.spark}
                  hero={card.hero}
                  takeaway={card.label === "Bookings" ? <PanelTakeaway panelKey="pacing" say={say} /> : null}
                />
              ))}
            </div>

            {/* ─── DAILY TREND CHART ─── */}
            <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="gi-panel-title text-gray-900 dark:text-white">Daily Trend</h3>
                <div className="flex flex-wrap gap-1.5">
                  {TREND_LINES.map((l) => (
                    <button
                      key={l.key}
                      type="button"
                      onClick={() => toggleTrendLine(l.key)}
                      className={`px-2.5 py-1 rounded-full text-xs font-medium transition-all ${
                        trendLines[l.key]
                          ? "text-white shadow-sm"
                          : "bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400"
                      }`}
                      style={trendLines[l.key] ? { backgroundColor: l.color } : undefined}
                    >
                      {l.label}
                    </button>
                  ))}
                </div>
              </div>
              {trendLoading ? (
                <SkeletonChart height={280} className="rounded-lg" />
              ) : trend.length === 0 ? (
                <div className="h-64 flex items-center justify-center text-gray-400 text-sm">No data for this period</div>
              ) : (
                <ResponsiveContainer width="100%" height={280}>
                  <AreaChart data={trend} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                    <defs>
                      {TREND_LINES.filter(l => trendLines[l.key]).map(l => (
                        <linearGradient key={l.key} id={`grad-${l.key}`} x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={l.color} stopOpacity={0.2} />
                          <stop offset="95%" stopColor={l.color} stopOpacity={0} />
                        </linearGradient>
                      ))}
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? "#374151" : "#E5E7EB"} />
                    <XAxis dataKey="day" tickFormatter={fmtDay} tick={{ fontSize: 11 }} stroke={isDarkMode ? "#6B7280" : "#9CA3AF"} />
                    <YAxis tick={{ fontSize: 11 }} stroke={isDarkMode ? "#6B7280" : "#9CA3AF"} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: isDarkMode ? "#1F2937" : "#fff",
                        border: `1px solid ${isDarkMode ? "#374151" : "#E5E7EB"}`,
                        borderRadius: 8,
                        fontSize: 12,
                      }}
                      labelFormatter={fmtDay}
                    />
                    {TREND_LINES.filter(l => trendLines[l.key]).map(l => (
                      <Area
                        key={l.key}
                        type="monotone"
                        dataKey={l.key}
                        stroke={l.color}
                        fill={`url(#grad-${l.key})`}
                        strokeWidth={2}
                        dot={false}
                      />
                    ))}
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </div>

            {/* ═══ TIER 3: GUEST STORY ═══
                Most-synthesized narrative panels first (after the money KPIs +
                trend). Funnel → Segments → Ghost Demand / Decision Cycle /
                Returning → Known People. Ghost Demand is the most valuable panel
                in the product, so it lives here at full width — not buried on a
                last tab. Tech signals come later (Tier 5). */}
            <div className="pt-2">
              <h2 className="gi-label text-gray-400 dark:text-gray-500">Guest Story</h2>
            </div>

            {/* ─── VISITOR FUNNEL (leads with its sentence) ─── */}
            <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-5">
              <PanelTakeaway panelKey="funnel" say={say} />
              <h3 className="gi-panel-title text-gray-900 dark:text-white mb-4">Visitor Funnel</h3>
              {funnelLoading ? (
                <SkeletonChart height={96} className="rounded-lg" />
              ) : !funnel || funnelStages.length === 0 ? (
                <div className="h-24 flex items-center justify-center text-gray-400 text-sm">No data for this period</div>
              ) : (
                <div className="overflow-x-auto min-[1100px]:overflow-visible -mx-1 px-1">
                  <div className="flex items-center gap-2 min-w-max min-[1100px]:min-w-0 min-[1100px]:w-full max-[499px]:flex-col max-[499px]:items-stretch max-[499px]:min-w-0 max-[499px]:w-full">
                    {funnelStages.map((stage, i) => {
                      const maxCount = funnelStages[0]?.count || 1;
                      const widthPct = Math.max(20, (stage.count / maxCount) * 100);
                      return (
                        <React.Fragment key={stage.label}>
                          {i > 0 && (
                            <div className="flex flex-col items-center text-xs text-gray-400 dark:text-gray-500 shrink-0">
                              <span>{stage.rate}%</span>
                              <span className="text-gray-300 dark:text-gray-600 max-[499px]:inline-block max-[499px]:rotate-90">&rarr;</span>
                            </div>
                          )}
                          <div
                            className={`${stage.color} rounded-lg px-3 py-3 text-center transition-all shrink-0 min-[1100px]:shrink min-[1100px]:flex-1 w-[140px] min-[1100px]:w-[var(--funnel-stage-w)] min-[1100px]:min-w-[80px] max-[499px]:w-full max-[499px]:min-w-0`}
                            style={{ '--funnel-stage-w': `${widthPct}%` }}
                          >
                            <div className="text-lg font-bold text-gray-900 dark:text-white">{fmtNum(stage.count)}</div>
                            <div className="text-[11px] font-medium text-gray-600 dark:text-gray-300 mt-0.5">{stage.label}</div>
                          </div>
                        </React.Fragment>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Segments — leads with its sentence (its segment buttons filter the
                Visitor Sessions table further below). */}
            <CdpVisitorSegments
              clientId={selectedClientId}
              rangeStart={rangeStart}
              rangeEnd={rangeEnd}
              includeBots={includeBots}
              onSelectSegment={setActiveSegment}
              activeSegment={activeSegment}
              say={say}
            />

            {/* Ghost Demand / Decision Cycle / Returning — each sub-panel leads
                with its own sentence. Ghost Demand is the most valuable panel in
                the product, surfaced here at full width. */}
            <CdpInsightsTab
              clientId={selectedClientId}
              rangeStart={rangeStart}
              rangeEnd={rangeEnd}
              includeBots={includeBots}
              isDarkMode={isDarkMode}
              say={say}
            />

            {/* Known People — leads with its sentence. */}
            <CdpPeopleTab
              clientId={selectedClientId}
              rangeStart={rangeStart}
              rangeEnd={rangeEnd}
              includeBots={includeBots}
              isDarkMode={isDarkMode}
              onSelectPerson={(p) => setSelectedPerson({ personId: p.person_id })}
              say={say}
            />

            {/* Visitor Sessions — the visitor-level drill-down table. Kept (not
                removed); sits after the guest-story panels so the synthesized
                narrative leads and the raw session list follows. Segment buttons
                above still filter it via activeSegment. */}
            <CdpSessionTable
              clientId={selectedClientId}
              rangeStart={rangeStart}
              rangeEnd={rangeEnd}
              includeBots={includeBots}
              onSelectSession={(s) => setSelectedSession(s)}
              activeSegment={activeSegment}
            />

            {/* ═══ TIER 4: ACQUISITION & CONTENT ═══ */}
            <div className="pt-2">
              <h2 className="gi-label text-gray-400 dark:text-gray-500">Acquisition & Content</h2>
            </div>
            <CdpChannelsTab
              clientId={selectedClientId}
              rangeStart={rangeStart}
              rangeEnd={rangeEnd}
              includeBots={includeBots}
              isDarkMode={isDarkMode}
              say={say}
            />
            <CdpContentTab
              clientId={selectedClientId}
              rangeStart={rangeStart}
              rangeEnd={rangeEnd}
              includeBots={includeBots}
              say={say}
            />

            {/* ═══ TIER 5: AI & TECHNOLOGY ═══ */}
            <div className="pt-2">
              <h2 className="gi-label text-gray-400 dark:text-gray-500">AI & Technology</h2>
            </div>
            <CdpAiTechTab
              clientId={selectedClientId}
              rangeStart={rangeStart}
              rangeEnd={rangeEnd}
              includeBots={includeBots}
              isDarkMode={isDarkMode}
              say={say}
            />

          </div>
        </main>

      {/* ─── Session Detail Slide-out ─── */}
      {selectedSession && (
        <CdpSessionDetail
          clientId={selectedClientId}
          sessionId={selectedSession.session_id}
          onClose={() => setSelectedSession(null)}
        />
      )}

      {/* ─── Person Profile Slide-out ─── */}
      {selectedPerson && (
        <PersonProfile
          clientId={selectedClientId}
          personId={selectedPerson.personId}
          cvId={selectedPerson.cvId}
          onClose={() => setSelectedPerson(null)}
        />
      )}
    </>
  );
}
